import { useEffect } from 'react';

/**
 * NavigationGuard component that intercepts navigation attempts
 * and checks for unsaved changes before allowing navigation.
 */
const NavigationGuard = ({
  hasUnsavedChanges,
  showUnsavedChangesModal,
  setShowUnsavedChangesModal,
  setPendingAction,
  children,
}) => {
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    // Intercept all link clicks on the page
    const handleClick = event => {
      const target = event.target.closest('a[href]');

      if (target && !showUnsavedChangesModal) {
        // Check if it's an external link or internal navigation
        const href = target.getAttribute('href');

        // Skip if it's a javascript: link or empty href
        if (!href || href.startsWith('javascript:') || href === '#') {
          return;
        }

        // Block the navigation
        event.preventDefault();
        event.stopPropagation();

        setPendingAction(() => () => {
          // Navigate to the original destination
          window.location.href = href;
        });

        setShowUnsavedChangesModal(true);
      }
    };

    // Intercept form submissions that might navigate away
    const handleSubmit = event => {
      if (!showUnsavedChangesModal) {
        const form = event.target;
        const action = form.getAttribute('action');

        // If form has an action that would navigate away
        if (action && action !== window.location.pathname) {
          event.preventDefault();
          event.stopPropagation();

          setPendingAction(() => () => {
            // Re-submit the form
            form.submit();
          });

          setShowUnsavedChangesModal(true);
        }
      }
    };

    // Intercept common keyboard shortcuts for navigation
    const handleKeyDown = event => {
      if (!showUnsavedChangesModal) {
        // Common navigation keyboard shortcuts
        const isNavShortcut =
          ((event.ctrlKey || event.metaKey) &&
            (event.key === 'r' || // Refresh
            event.key === 'R' || // Refresh
            event.key === 'w' || // Close tab
            event.key === 'W' || // Close tab
            event.key === 'l' || // Address bar
              event.key === 'L')) || // Address bar
          (event.altKey &&
            (event.key === 'ArrowLeft' || // Back
              event.key === 'ArrowRight')); // Forward

        if (isNavShortcut) {
          event.preventDefault();
          event.stopPropagation();

          setPendingAction(() => () => {
            if (event.key === 'r' || event.key === 'R') {
              window.location.reload();
            } else if (event.altKey && event.key === 'ArrowLeft') {
              window.history.back();
            } else if (event.altKey && event.key === 'ArrowRight') {
              window.history.forward();
            } else {
              console.log('Navigation shortcut intercepted');
            }
          });

          setShowUnsavedChangesModal(true);
        }
      }
    };

    // Add event listeners
    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('submit', handleSubmit, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    hasUnsavedChanges,
    showUnsavedChangesModal,
    setShowUnsavedChangesModal,
    setPendingAction,
  ]);

  return children;
};

export default NavigationGuard;
